// VISUALIZE statement plumbing shared by the wasm host bridge and the
// runner. Three concerns, all deliberately free of UI / vega imports so
// wasm-bridge.ts can use them without dragging chart code into its
// chunk:
//
//   1. `looksLikeVisualize` — cheap syntactic detection of stats_duck
//      `VISUALIZE … DRAW` statements (bare or WITH-prefixed, as PROC
//      SGPLOT emits them). Detection is attempt-and-fallback: a false
//      positive costs one failed plot attempt, never a broken query.
//   2. The plot sink — wasm-bridge hands finished plots here; the
//      runner registers a buffer for the duration of each run so plots
//      render AFTER the run's text output, in execution order.
//   3. `runVisualizeSql` — lazy gateway into the chart chunk (vega +
//      bedevere-wise UI) so users who never plot don't download it.

export interface PlotData {
  /** Vega-Lite v5 spec produced by stats_duck (parsed JSON). */
  spec: unknown;
  /** `layer_0`, `layer_1`, … → row arrays for vega named datasets. */
  datasets: Record<string, unknown[]>;
}

/// Thrown by the plot path when the VISUALIZE statement itself failed
/// or didn't produce the {spec, layer_sqls} envelope — i.e. the
/// detection was (or might as well have been) a false positive. The
/// bridge catches exactly this class and falls through to normal SQL
/// execution. Errors past the initial query (layer SQL failures, spec
/// parse) are real VISUALIZE failures and surface as SQL errors.
export class VisualizeFallbackError extends Error {
  constructor(cause: unknown) {
    super(cause instanceof Error ? cause.message : String(cause));
    this.name = 'VisualizeFallbackError';
  }
}

type PlotSink = (plot: PlotData) => void;

let sink: PlotSink | null = null;

/// Register (or clear, with null) the per-run plot buffer. The runner
/// sets it just before `kolistat.run` and clears it in a finally.
export function setPlotSink(cb: PlotSink | null): void {
  sink = cb;
}

/// Deliver a finished plot to the active run's buffer. Plots produced
/// outside a run (shouldn't happen — only the engine routes SQL through
/// the bridge) are dropped with a console warning.
export function emitPlot(plot: PlotData): void {
  if (!sink) {
    // eslint-disable-next-line no-console
    console.warn('[kolilang] plot produced outside a run — dropped');
    return;
  }
  sink(plot);
}

/// Execute a VISUALIZE statement through the lazily-loaded chart
/// machinery. Rejections with `VisualizeFallbackError` mean "not
/// actually a usable VISUALIZE — run it as plain SQL".
export async function runVisualizeSql(sql: string): Promise<PlotData> {
  const m = await import('./chart');
  return m.runVisualizeQuery(sql);
}

// --- detection --------------------------------------------------------

/// True when `sql` is (very likely) a stats_duck VISUALIZE statement:
/// after leading whitespace/comments it starts with the VISUALIZE
/// keyword, or starts with WITH and contains a top-level (outside
/// strings/comments/parens) VISUALIZE followed by DRAW. PROC SGPLOT
/// emits both shapes (`WITH __sgplot AS (…) VISUALIZE … DRAW …`).
/// False positives are cheap (attempt-and-fallback in the bridge);
/// the scanner mainly guards against VISUALIZE inside string literals
/// and subqueries.
export function looksLikeVisualize(sql: string): boolean {
  const s = stripLeadingTrivia(sql);
  if (/^visualize\b/i.test(s)) return true;
  if (!/^with\b/i.test(s)) return false;
  return hasTopLevelVisualizeDraw(s);
}

/// Drop leading whitespace, `--` line comments, and `/* */` block
/// comments so detection sees the first real token.
function stripLeadingTrivia(sql: string): string {
  let i = 0;
  const n = sql.length;
  for (;;) {
    while (i < n && /\s/.test(sql[i])) i += 1;
    if (sql.startsWith('--', i)) {
      const nl = sql.indexOf('\n', i);
      if (nl === -1) return '';
      i = nl + 1;
      continue;
    }
    if (sql.startsWith('/*', i)) {
      const end = sql.indexOf('*/', i + 2);
      if (end === -1) return '';
      i = end + 2;
      continue;
    }
    return sql.slice(i);
  }
}

/// Scan for a VISUALIZE token at paren depth 0 (outside quotes and
/// comments), then a DRAW token after it, also at depth 0.
function hasTopLevelVisualizeDraw(sql: string): boolean {
  let depth = 0;
  let sawVisualize = false;
  let i = 0;
  const n = sql.length;
  while (i < n) {
    const c = sql[i];
    if (c === "'" || c === '"') {
      // String literal / quoted identifier. Doubled quotes escape.
      const q = c;
      i += 1;
      while (i < n) {
        if (sql[i] === q) {
          if (sql[i + 1] === q) { i += 2; continue; }
          i += 1;
          break;
        }
        i += 1;
      }
      continue;
    }
    if (c === '-' && sql[i + 1] === '-') {
      const nl = sql.indexOf('\n', i);
      if (nl === -1) return sawVisualize && false;
      i = nl + 1;
      continue;
    }
    if (c === '/' && sql[i + 1] === '*') {
      const end = sql.indexOf('*/', i + 2);
      if (end === -1) break;
      i = end + 2;
      continue;
    }
    if (c === '(') { depth += 1; i += 1; continue; }
    if (c === ')') { depth = Math.max(0, depth - 1); i += 1; continue; }
    if (/[A-Za-z_]/.test(c)) {
      let j = i + 1;
      while (j < n && /[A-Za-z0-9_$]/.test(sql[j])) j += 1;
      if (depth === 0) {
        const word = sql.slice(i, j).toUpperCase();
        if (word === 'VISUALIZE') sawVisualize = true;
        else if (word === 'DRAW' && sawVisualize) return true;
      }
      i = j;
      continue;
    }
    i += 1;
  }
  return false;
}
